import { apiFetch } from '../../services/api';

export type CampaignStatus = 'draft' | 'scheduled' | 'sent' | 'failed';

export interface CampaignListItem {
  id: number;
  name: string;
  subject: string;
  status: CampaignStatus;
  scheduled_for: string | null;
  recipient_count: number | null;
  sent_by_username: string | null;
  updated_at: string;
}

export interface Campaign extends CampaignListItem {
  preheader: string;
  body_html: string;
  cta_text: string;
  cta_url: string;
  content_hash: string;
  is_tested: boolean;
  tested_at: string | null;
  tested_to: string;
  last_error: string;
  created_at: string;
}

export type CampaignInput = Pick<
  Campaign,
  'name' | 'subject' | 'preheader' | 'body_html' | 'cta_text' | 'cta_url'
>;

export interface MarketingOverview {
  configured: boolean;
  subscriber_count: number;
  send_delay_minutes: number;
  from_email: string;
  admin_email: string;
}

async function parse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  let data: any = null;
  try {
    data = await response.json();
  } catch {
    /* empty body */
  }
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    if (data?.error) message = data.error;
    else if (data?.detail) message = data.detail;
    else if (data && typeof data === 'object') {
      const first = Object.entries(data)[0];
      if (first) message = `${first[0]}: ${Array.isArray(first[1]) ? first[1][0] : first[1]}`;
    }
    throw new Error(message);
  }
  return data as T;
}

const BASE = '/api/marketing';

export const marketingApi = {
  overview: () => apiFetch(`${BASE}/overview/`).then((r) => parse<MarketingOverview>(r)),

  list: () => apiFetch(`${BASE}/campaigns/`).then((r) => parse<CampaignListItem[]>(r)),

  get: (id: number) => apiFetch(`${BASE}/campaigns/${id}/`).then((r) => parse<Campaign>(r)),

  create: (input: CampaignInput) =>
    apiFetch(`${BASE}/campaigns/`, { method: 'POST', body: JSON.stringify(input) }).then((r) =>
      parse<Campaign>(r),
    ),

  update: (id: number, input: Partial<CampaignInput>) =>
    apiFetch(`${BASE}/campaigns/${id}/`, { method: 'PATCH', body: JSON.stringify(input) }).then((r) =>
      parse<Campaign>(r),
    ),

  remove: (id: number) =>
    apiFetch(`${BASE}/campaigns/${id}/`, { method: 'DELETE' }).then((r) => parse<void>(r)),

  duplicate: (id: number) =>
    apiFetch(`${BASE}/campaigns/${id}/duplicate/`, { method: 'POST' }).then((r) => parse<Campaign>(r)),

  preview: (input: Partial<CampaignInput>) =>
    apiFetch(`${BASE}/preview/`, { method: 'POST', body: JSON.stringify(input) }).then((r) =>
      parse<{ html: string }>(r),
    ),

  sendTest: (id: number, toEmail: string) =>
    apiFetch(`${BASE}/campaigns/${id}/test/`, {
      method: 'POST',
      body: JSON.stringify({ to_email: toEmail }),
    }).then((r) => parse<Campaign>(r)),

  send: (id: number, confirmSubject: string, contentHash: string) =>
    apiFetch(`${BASE}/campaigns/${id}/send/`, {
      method: 'POST',
      body: JSON.stringify({ confirm_subject: confirmSubject, content_hash: contentHash }),
    }).then((r) => parse<Campaign>(r)),

  cancel: (id: number) =>
    apiFetch(`${BASE}/campaigns/${id}/cancel/`, { method: 'POST' }).then((r) => parse<Campaign>(r)),

  /** Image goes up as a data URL; the server resizes it and returns a public https URL. */
  uploadImage: (dataUrl: string) =>
    apiFetch(`${BASE}/images/`, { method: 'POST', body: JSON.stringify({ data_url: dataUrl }) }).then((r) =>
      parse<{ url: string }>(r),
    ),
};

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
}
